import { doc, getDoc, getDocFromServer, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { encryptProfileFields } from '../encryptionUtil';
import { getAuth } from 'firebase/auth'; // Add missing import
import { ROUTES } from '../App'; // Import ROUTES
import { getFirestore, updateDoc as updateDocRole, getDoc as getDocRole } from 'firebase/firestore';

const dbRoles = getFirestore();

class UserService {
  constructor() {
    // Initialize any necessary properties
  }

  /**
   * Ensures all required onboarding flags are correctly set
   */
  async ensureOnboardingFlags(userId) {    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Checking onboarding flags for user:', userId);
      }
      const userDoc = await getDocFromServer(doc(db, 'users', userId));
        if (!userDoc.exists()) {
        if (process.env.NODE_ENV === 'development') {
          console.log('User document not found - creating a new one');
        }
        
        // Get the auth user to access their basic info
        const auth = getAuth();
        const user = auth.currentUser;
          if (!user) {
          if (process.env.NODE_ENV === 'development') {
            console.log('No authenticated user found, aborting');
          }
          return false;
        }
        
        // Create a basic user document with default values
        const basicUserData = {
          uid: userId,
          email: user.email || '',
          displayName: user.displayName || '',
          provider: user.providerData?.[0]?.providerId || 'unknown',
          lastLogin: new Date().toISOString()
        };
        const encryptedBasicUserData = encryptProfileFields(basicUserData);
          try {
          // Create the document in Firestore
          await setDoc(doc(db, 'users', userId), encryptedBasicUserData, { merge: true });
          if (process.env.NODE_ENV === 'development') {
            console.log('Basic user document created due to missing record');
          }
          return false; // Return false to trigger onboarding
        } catch (createError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error creating replacement user document:', createError);
          }
          return false;
        }
      }
      
      // Rest of the existing function
      const userData = userDoc.data();
        // Check if onboardingComplete flag is missing or needs repair
      if (userData.onboardingComplete === undefined || userData.onboardingComplete === false) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Repairing missing or false onboardingComplete flag');
        }
        
        // Determine onboarding status based on existing flags
        const hasConsent = userData.consentGiven === 'true';
        const hasProfileComplete = !!userData.profileComplete;
        
        // CRITICAL FIX: Default hasBillingComplete to false instead of undefined
        // This ensures we don't get undefined in isOnboardingComplete calculation
        let hasBillingComplete = false;
        
        // Check for subscription data - either in the old or new format
        if ((userData.subscription && userData.subscription.status === 'active') ||
            (userData.subscriptionStatus && userData.subscriptionStatus.status === 'active')) {
          hasBillingComplete = true;
        }
        // Also check billing complete flag if it exists
        else if (!!userData.billingComplete || !!userData.selectedPlan) {
          hasBillingComplete = true;
        }
          // Calculate onboarding status - if all three key steps are done, onboarding is complete
        const isOnboardingComplete = hasConsent && hasProfileComplete && hasBillingComplete;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Onboarding status check:', {
            hasConsent, hasProfileComplete, hasBillingComplete, isOnboardingComplete
          });
        }
        
        // CRITICAL FIX: We know isOnboardingComplete will be boolean now
        // Update the user document with explicit flag
        await updateDoc(doc(db, 'users', userId), {
          onboardingComplete: isOnboardingComplete,
          lastUpdated: new Date()        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Updated onboardingComplete flag to:', isOnboardingComplete);
        }
        return isOnboardingComplete;
      }
      
      return userData.onboardingComplete === true;    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error ensuring onboarding flags:', error);
      }
      return false;
    }
  }

  /**
   * Rebuilds a user profile from scratch, safely sanitizing Firebase Auth objects
   */  async rebuildUserProfile(authUser, profileData) {
    if (!authUser || !authUser.uid) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Invalid auth user provided to rebuildUserProfile");
      }
      return null;
    }

    try {
      let cleanedData = {};
      
      // Add only the minimal necessary attributes from the auth user
      cleanedData.email = authUser.email || '';
      cleanedData.displayName = authUser.displayName || '';
      cleanedData.photoURL = authUser.photoURL || '';
      cleanedData.uid = authUser.uid; // Safe to store for reference
      
      // Add the profile data, but first sanitize it to avoid Firebase Auth internals
      if (profileData) {
        // List of properties to exclude from profileData
        const excludeProps = [
          // Firebase Auth internal properties
          'proactiveRefresh', 'reloadUserInfo', 'reloadListener', 'stsTokenManager',
          'apiKey', 'appName', 'authDomain', 'uid', 'auth', 'accessToken',
          // Function properties
          'delete', 'getIdToken', 'getIdTokenResult', 'reload', 'toJSON',
          // Other non-serializable properties
          'providerId', 'refreshToken', 'tenantId', 'metadata'
        ];
        
        // Copy profileData properties, excluding the ones in the exclude list
        Object.keys(profileData).forEach(key => {
          if (!excludeProps.includes(key) && 
              typeof profileData[key] !== 'function' && 
              profileData[key] !== undefined) {
            cleanedData[key] = profileData[key];
          }
        });
      }
      
      cleanedData = encryptProfileFields(cleanedData);

      // Set essential profile fields
      cleanedData.profileComplete = true;
      cleanedData.updatedAt = new Date().toISOString();
      
      // Critical fix: Ensure privacy consent flags are explicitly set
      if (profileData && profileData.consentGiven === 'true') {
        cleanedData.consentGiven = 'true';
        cleanedData.needsPrivacyConsent = false;      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log("Saving sanitized user data to Firestore:", 
                    Object.keys(cleanedData).join(', '));
      }
      
      // Save the sanitized data to Firestore
      const userDocRef = doc(db, 'users', authUser.uid);
      await setDoc(userDocRef, cleanedData, { merge: true });
        return cleanedData;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error rebuilding user profile:", error);
      }
      throw error;
    }
  }

  /**
   * Explicitly force onboarding complete for user with active subscription
   */  async forceCompleteOnboarding(userId) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Force completing onboarding for user:', userId);
      }
      
      await updateDoc(doc(db, 'users', userId), {
        onboardingComplete: true,
        profileComplete: true,
        billingComplete: true,
        lastStep: 'completed',
        lastUpdated: new Date()      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Successfully forced onboarding completion');
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error forcing onboarding completion:', error);
      }
      return false;
    }
  }

  /**
   * Get user profile data from Firestore
   */
  async getUserProfile(userId) {
    if (!userId) return null;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return null;
        return userDoc.data();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting user profile:', error);
      }
      return null;
    }
  }

  /**
   * Recreate user document if missing
   * This can be called when app detects the user is authenticated but the document is missing
   */
  async recreateUserDocument(user) {
    if (!user?.uid) return false;
      try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Attempting to recreate user document for ${user.uid}`);
      }
      const userDoc = await getDocFromServer(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
        if (process.env.NODE_ENV === 'development') {
          console.log('User document already exists, no need to recreate');
        }
        return true;
      }
      
      // Create only safe identity data; merge so a transient read cannot erase a profile.
      const basicUserData = {
        uid: user.uid,
        displayName: user.displayName || '',
        email: user.email || '',
        provider: user.providerData?.[0]?.providerId || 'unknown',
        lastLogin: new Date().toISOString()
      };
      const encryptedBasicUserData = encryptProfileFields(basicUserData);
        // Create the document in Firestore
      await setDoc(doc(db, 'users', user.uid), encryptedBasicUserData, { merge: true });
      if (process.env.NODE_ENV === 'development') {
        console.log('User document successfully recreated');
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error recreating user document:', error);
      }
      return false;
    }
  }
  // Add missing repairOnboardingFlags function
  async repairOnboardingFlags(userId, userData) {
    if (!userId || !userData) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('repairOnboardingFlags called without userId or userData');
      }
      return userData;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Repairing missing or false onboardingComplete flag');
    }
    
    // Clone userData to avoid mutations
    const user = { ...userData };
    
    // Check if flags are consistent
    const hasConsent = user.consentGiven === 'true';
    const hasProfileComplete = !!user.profileComplete;
    const hasRole = user.role && user.role.trim() !== '';
    const isPracticeManager = user.role === 'Practice Manager';
    
    // MODIFIED: Role-aware billing check
    const hasBillingComplete = isPracticeManager ? true : user.billingComplete === true; // Practice Managers don't need billing
      const shouldBeOnboardingComplete = hasConsent && hasProfileComplete && hasRole && hasBillingComplete;
    const isOnboardingComplete = user.onboardingComplete === true;

    if (process.env.NODE_ENV === 'development') {
      console.log("Onboarding status check:", {
        hasConsent,
        hasProfileComplete,
        hasRole,
        isPracticeManager, // ADDED: Log Practice Manager status
        hasBillingComplete, // This will be true for Practice Managers
        isOnboardingComplete: shouldBeOnboardingComplete
      });
    }    // If should be complete but isn't marked as such, update it
    if (shouldBeOnboardingComplete && !isOnboardingComplete) {
      if (process.env.NODE_ENV === 'development') {
        console.log("Repairing missing or false onboardingComplete flag");
      }
      try {
        await updateDoc(doc(db, 'users', userId), {
          onboardingComplete: true,
          lastUpdated: new Date().toISOString()
        });        return { ...userData, onboardingComplete: true };
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error updating onboardingComplete flag:", error);
        }
      }
    }
    
    return user;
  }
}

/**
 * Update user role in Firestore
 * @param {string} userId - The user's UID
 * @param {string} role - The role to set
 * @returns {Promise<void>}
 */
export const updateUserRole = async (userId, role) => {
  try {
    const userDoc = doc(dbRoles, 'users', userId);
    await updateDocRole(userDoc, {
      role: role,
      lastUpdated: new Date()    });
    if (process.env.NODE_ENV === 'development') {
      console.log('User role updated successfully:', role);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating user role:', error);
    }
    throw error;
  }
};

/**
 * Get user role from Firestore
 * @param {string} userId - The user's UID
 * @returns {Promise<string|null>} - The user's role or null if not found
 */
export const getUserRole = async (userId) => {
  try {
    const userDoc = doc(dbRoles, 'users', userId);
    const userSnapshot = await getDocRole(userDoc);
    
    if (userSnapshot.exists()) {
      const userData = userSnapshot.data();
      return userData.role || null;    }
    return null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching user role:', error);
    }
    throw error;
  }
};

/**
 * Get current authenticated user's role
 * @returns {Promise<string|null>} - The current user's role or null
 */
export const getCurrentUserRole = async () => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    return null;
  }
  
  return await getUserRole(currentUser.uid);
};

/**
 * Check if current user has a specific role
 * @param {string} requiredRole - The role to check for
 * @returns {Promise<boolean>} - True if user has the role
 */
export const hasRole = async (requiredRole) => {
  const userRole = await getCurrentUserRole();
  return userRole === requiredRole;
};

/**
 * Check if user is a practice manager
 * @returns {Promise<boolean>} - True if user is a practice manager
 */
export const isPracticeManager = async () => {
  return await hasRole('Practice Manager');
};

/**
 * Check if user is a practitioner (not practice manager)
 * @returns {Promise<boolean>} - True if user is a practitioner
 */
export const isPractitioner = async () => {
  const userRole = await getCurrentUserRole();
  const practitionerRoles = ['Dentist', 'Hygienist', 'Therapist', 'Dental Nurse'];
  return practitionerRoles.includes(userRole);
};

export const userService = new UserService();